import React, { useState, useEffect } from 'react';
import { post } from '../../services/ApiService';

function StepEleven({ formData, onNext }) {
    const [hasP45, setHasP45] = useState(formData.hasP45 || '');
    const [p45File, setP45File] = useState(null);
    const [taxCode, setTaxCode] = useState(formData.taxCode || '');
    const [error, setError] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadedFile, setUploadedFile] = useState(formData.p45File || null);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    // Initialize from formData
    useEffect(() => {
        if (formData.hasP45) {
            setHasP45(formData.hasP45);
        }
        if (formData.p45File) {
            setUploadedFile(formData.p45File);
        }
        if (formData.taxCode) {
            setTaxCode(formData.taxCode);
        }
    }, [formData]);

    const handleContinue = () => {
        if (!hasP45) {
            setError('Please select Yes or No.');
            return;
        }

        if (hasP45 === 'yes' && !uploadedFile) {
            setError('Please upload your recent P45.');
            return;
        }

        if (hasP45 === 'no' && !taxCode.trim()) {
            setError('Please provide your Tax Code.');
            return;
        }

        setError('');
        onNext({ hasP45, p45File: uploadedFile, taxCode });
    };

    const onUpload = async (file) => {
        const formDataObj = new FormData();
        formDataObj.append("file", file);
        
        try {
            setUploadProgress(0);

            // Simulate progress
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => prev < 90 ? prev + 10 : 90);
            }, 200);

            const res = await post("home/file-upload", formDataObj, true);
            console.log(res);
            clearInterval(progressInterval);
            setUploadProgress(100);
            
            // Small delay to show 100% completion
            await new Promise(resolve => setTimeout(resolve, 300));
            
            if (res.success) {
                setUploadedFile(res.file);
                return res.file;
            } else {
                throw new Error(res.message || "Upload failed");
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            setUploadProgress(0);
            throw error;
        }
    };

    const onRemove = async () => {
        const filename = uploadedFile;
        if (!filename) return;

        try {
            const res = await post("home/file-remove", { filename: filename });
            if (res.success) {
                setUploadedFile(null);
                setP45File(null);
                setUploadProgress(0);
            }
            return res;
        } catch (error) {
            console.error("Error in file removal:", error);
            throw error;
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setP45File(file);

        try {
            await onUpload(file);
        } catch (error) {
            setError('Failed to upload P45 file. Please try again.');
            setP45File(null);
        }
    };

    const renderFilePreview = () => {
        const fileName = uploadedFile;
        const progress = uploadProgress;
        const isUploaded = !!fileName;
        
        const displayFile = p45File || (fileName ? { name: fileName } : null);
        
        if (!displayFile) return null;

        const isNewFile = p45File && !fileName;
        const isExistingFile = !p45File && fileName;
        
        const isImage = p45File ? p45File.type.startsWith('image/') : 
                              fileName && /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName);
        
        const previewURL = p45File ? URL.createObjectURL(p45File) : 
                          `${backendUrl}/uploads/${fileName}`;

        return (
            <div className="file_prev">
                <div className="d-flex align-items-center justify-content-between gap-3 mt-2">
                    <div className="d-flex align-items-center gap-2">
                        {isImage && (
                            <img
                                src={previewURL}
                                alt="preview"
                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        )}
                        <div>
                            <span>{displayFile.name}</span>
                            {isUploaded && (
                                <div className="text-success small">
                                    {isExistingFile ? 'Previously uploaded: ' : 'Uploaded: '}{fileName}
                                </div>
                            )}
                            {isNewFile && progress === 0 && (
                                <div className="text-warning small">Ready to upload</div>
                            )}
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {progress > 0 && progress < 100 && (
                            <div className="progress" style={{ width: '100px', height: '8px' }}>
                                <div 
                                    className="progress-bar" 
                                    style={{ width: `${progress}%` }}
                                ></div>
                            </div>
                        )}
                        <button 
                            className="border-0 bg-transparent text-danger" 
                            onClick={onRemove}
                            disabled={progress > 0 && progress < 100}
                        >
                            <img src="/assets/bin_icon.png" alt="delete" style={{ width: '15px' }} />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const isFormValid = hasP45 === 'yes' ? !!uploadedFile : hasP45 === 'no' ? !!taxCode.trim() : false;

    return (
        <div className="step-form">
            <h6 className="mb-4">P45 & Tax Code</h6>

            {error && <div className="alert alert-danger">{error}</div>}

            <p>Do you have a recent P45? <span className="text-danger">*</span></p>

            <div className="row">
                <div className="col-lg-6">
                    <div className="form-check mb-2">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="p45"
                            id="p45Yes"
                            value="yes"
                            checked={hasP45 === 'yes'}
                            onChange={() => setHasP45('yes')}
                        />
                        <label className="form-check-label" htmlFor="p45Yes">
                            Yes
                        </label>
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="form-check mb-3">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="p45"
                            id="p45No"
                            value="no"
                            checked={hasP45 === 'no'}
                            onChange={() => setHasP45('no')}
                        />
                        <label className="form-check-label" htmlFor="p45No">
                            No
                        </label>
                    </div>
                </div>
            </div>

            {hasP45 === 'yes' && !uploadedFile && (
                <div className="mb-3">
                    <label className="form-label">Upload your recent P45 <span className="text-danger">*</span></label>
                    <div className="upl_file">
                        <input
                            type="file"
                            className="form-control"
                            onChange={handleFileChange}
                        />
                        <p className="drag_text text-center">
                            Drag your file(s) here or <b>Browse</b>
                            <span className="d-block">Max 1.5mb file size</span>
                        </p>
                    </div>
                </div>
            )}

            {/* Show file preview when file is uploaded (existing or new) */}
            {(uploadedFile || p45File) && renderFilePreview()}


            {hasP45 === 'no' && (
                <div className="mb-3">
                    <label className="form-label">Tax Code <span className="text-danger">*</span></label>
                    <textarea
                        className="form-control"
                        rows="3"
                        value={taxCode}
                        onChange={e => setTaxCode(e.target.value)}
                        placeholder="Enter your tax code..."
                    />
                </div>
            )}

            <div className="text-end mt-5">
                <button
                    className="btn btn-dark px-4"
                    onClick={handleContinue}
                    disabled={!isFormValid}
                >
                    Next
                </button>
            </div>
        </div>
    );
}

export default StepEleven;